import React, { useCallback, useEffect, useState, useMemo, Dispatch } from 'react';
import {
    Slider,
    Button,
    MultiSelect,
    MultiSelectOption, Loader, RangePicker,
    SelectOption, Select
} from '@kaleyra/emmy';
import Styles from './voicemail.module.scss';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import moment from 'moment';
import {debounce, isEmpty} from 'lodash';
import ErrorIcon from "../../../assets/error.png";
import {apiBaseUrl, networkCall} from "../../../utils/networkCall";
import {statusOptions} from "../constants";
import { useDispatch, useSelector } from 'react-redux';
import { IStateType } from '../../../store/models/root.interface';
import {
    fetchCallLogsIds
} from '../../../store/actions/soundManager.action';
import {
        getIVRFlowName,
} from '../../../store/actions/analyticsReport.action';


export type MapStateToPropsType = {
    params: any;
    flowLogsFilterFlowNames: any,
    flowLogsFilterInProgress: boolean | undefined,
};

export type MapDispatchToPropsType = {
    closeFilterModal: () => void;
    applyFilters: (filters: any) => void;
    loadFlowLogsFilterFlowNames: (filter: any) => void,
};
const format = 'YYYY-MM-DD HH:mm:ss';

const LIMIT: number = 25;
let PAGE: number = 0;

const FilterModal: React.FC<MapStateToPropsType & MapDispatchToPropsType> = (
    {
        closeFilterModal,
        params,
        applyFilters,
        loadFlowLogsFilterFlowNames,
        flowLogsFilterFlowNames,
        flowLogsFilterInProgress
    }) => {

    const [flowName, setFlowName] = useState<string[] | undefined>(undefined);
    const [status, setStatus] = useState<string|undefined>(undefined);
    const [showNumberError, setShowNumberError] = useState<boolean>(false);
    const { dateRangeTags, setDateRangeTags,numberTags, setNumberTags, statusTags, setStatusTags, callReferenceIdTags, setCallReferenceIdTags, flowNameTags, setFlowNameTags, filterDropdowns } = params;
    const [defaultFlowName] = useState<any>(flowNameTags?.length ? flowNameTags : undefined);
    const [dateRange, setDateRange] = useState([
        moment()
            .subtract(6, 'days')
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ss'),
        moment(Date.now()).format('YYYY-MM-DD HH:mm:ss'),
    ]);
    const [number, setNumber] = useState<string[]>([]);
    const [callReferenceId, setCallReferenceId] = useState<string[]>([]);
    const { callLogsIdsData } = useSelector(
        (state: IStateType) => state.callLogsIdsReducer
    );

    const dispatch: Dispatch<any> = useDispatch();

    const [defaultCallId, setDefaultCallId] = useState<any>(undefined);
    const [callIdSearchValue, setCallIdSearchValue] = useState<any>("");

    useEffect(()=>{
        dispatch(fetchCallLogsIds({
            limit: LIMIT,
            offset: 0,
            call_id:""
        }));
    },[]);

    useEffect(()=>{
        dispatch(getIVRFlowName()); 
    },[]);

    const debouncedChange = React.useCallback(
        debounce((value:string = "") => {
            dispatch(fetchCallLogsIds({
                limit: LIMIT,
                offset: 0,
                call_id:value
            }));
        }, 400),
        []
    );

    let {
        ivrFlowNameList,
    } = useSelector((state: IStateType) => state.analyticsReportReducer);

    const { t } = useTranslation();
    useEffect(() => {
        if (!isEmpty(dateRangeTags)) {
            setDateRange(dateRangeTags);
        }
        if (numberTags) setNumber(numberTags);
        if (statusTags) setStatus(statusTags);
        if (callReferenceIdTags) setCallReferenceId(callReferenceIdTags);
        if (flowNameTags) setFlowName(flowNameTags);
    }, []);
    const handleDateChange = (date: any, dateString: any) => {
        setDateRange(dateString);
    };

    function disabledDate(current: any) {
        // Can not select days after today or before 6 days
        return (
            current &&
            (current > moment().endOf('day') ||
                current < moment().subtract(6, 'days').startOf('day'))
        );
    }

    const selectChangeHandler = useCallback((val) => {
        setStatus(val);
    }, []);

    const handleSave = useCallback(() => {

        let filters:Record<string, any> = {};
        if (number && number.length) {
            filters.call_from = number;
            setNumberTags(number);
        } else {
            delete filters.call_from;
            setNumberTags([]);
        }

        if(callReferenceId && callReferenceId.length){
            filters.call_id = callReferenceId;
            setCallReferenceIdTags(callReferenceId);
        } else {
            delete filters.call_id;
            setCallReferenceIdTags([]);
        }

        !isEmpty(flowName) ? filters.flow_name = flowName : delete filters.flow_name;

        if (status !== '') {
            filters.play_status = status;
        } else {
            delete filters.play_status;
        }

        if (!isEmpty(dateRange)) {
            filters.dateRange = dateRange;
        }

        applyFilters(filters);

        // set the tags
        setStatusTags(filters.play_status);
        setFlowNameTags(filters.flow_name);
        setCallReferenceIdTags(callReferenceId);
        setNumberTags(number);
        setDateRangeTags(dateRange);

        closeFilterModal();
    }, [dateRange, status, flowName, callReferenceId, number]);

    const enableSaveButton = useMemo(() => {
        return status || !isEmpty(dateRange) || flowName;
    }, [status, dateRange, flowName]);

    const renderFooter = () => {
        return (
            <div className={Styles.footer}>
				<span className={Styles.cancelBtn}>
					<Button type="tertiary" onClick={() => closeFilterModal()}>
						{t('mainCancel')}
					</Button>
				</span>
                <Button
                    disabled={!enableSaveButton}
                    type="primary"
                    onClick={handleSave}
                >
                    {t('BLD01036')}
                </Button>
            </div>
        );
    };

    const multiSelectValidation = useCallback((values: any) => {
        let regex = /^\+?\d{0,14}([,]{1}[\d]{1,10})?$/;
        let valid = true;
        if(!values.length) setShowNumberError(false);
        values.forEach((element: any) => {
            if (element?.length && !regex.test(element)) {
                valid = false;
            }
        });
        if (!valid) return;
        else setNumber([...values]);
    },[setNumber,setShowNumberError]);

    const validateNumbers = useCallback((values: any = undefined) => {
        if (values) {
            let numbers = values;
            if (Array.isArray(values)) numbers = values.join(',');
            if (numbers) {
                networkCall(
                    `post`,
                    `/validate-numbers`,
                    apiBaseUrl.baseUrl,
                    true,
                    true,
                    {
                        number: encodeURI(numbers),
                    }
                ).then((res: any) => {
                    setShowNumberError(res?.code !== '200');
                });
            }
        } else setShowNumberError(false);
    },[]);

    useEffect(() => {
        loadFlowLogsFilterFlowNames({});
    }, [])

    return (
        <Slider
            title={t('mainFilter')}
            containerId="root-modal"
            onClose={() => closeFilterModal()}
            visibility={true}
            footer={renderFooter()}
        >
            { (flowLogsFilterInProgress === undefined || flowLogsFilterInProgress) && <Loader />}
            { flowLogsFilterInProgress === false && <div className={cn(Styles.filterModal, 'flowBuilderLogsFilterModal')}>
                <RangePicker
                    // onOk={(date, dateString) => { handleDateChange(date, dateString) }}
                    onChange={(date, dateString) => {
                        handleDateChange(date, dateString);
                    }}
                    className={cn(Styles.datePickerWrapper,"mt-3")}
                    showTime={{
                        hideDisabledOptions: true,
                    }}
                    format={format}
                    defaultValue={
                        isEmpty(dateRangeTags) || isEmpty(dateRangeTags[0])
                            ? [
                                moment(dateRange[0], format),
                                moment(dateRange[1], format),
                            ]
                            : [
                                moment(dateRangeTags[0], format),
                                moment(dateRangeTags[1], format),
                            ]
                    }
                    disabledDate={disabledDate}
                    placeholder={[t('startDate'), t('endDate')]}
                />
                {/* Enter Number */}
                <MultiSelect
                    className={"mt-3"}
                    placeholder={t('KLR00929')}
                    value={number}
                    defaultValue={number}
                    selectedValues={number}
                    onChange={(value: any) => {
                        multiSelectValidation(value);
                    }}
                    maxTagCount={6}
                    open={false}
                    onBlur={(e: any) => {
                        let numbToPass = [...number];
                        let mapToNumbers: any = [];
                        let regex = /^\+?\d{0,14}([,]{1}[\d]{1,10})?$/;
                        if ( e.target.value && regex.test(e.target.value)) {
                            numbToPass.push(e.target.value);
                            mapToNumbers = numbToPass.map(
                                (x: string) => x.split(',')[0]
                            );
                        }
                        if (mapToNumbers?.length) validateNumbers(mapToNumbers);
                        else return;
                    }}
                    onDeselect={(val: any) => {
                        if (number?.length) {
                            let mapNumbersToDeselect: any = [];
                            let filteredArrayTo = number.filter(
                                (e: string) => e !== val
                            );
                            if (filteredArrayTo?.length) {
                                mapNumbersToDeselect = filteredArrayTo.map(
                                    (x: string) => x.split(',')[0]
                                );
                                if (mapNumbersToDeselect?.length) {
                                    validateNumbers(mapNumbersToDeselect);
                                }
                            }
                        } else setShowNumberError(false);
                    }}
                    bottomLabelType={ showNumberError ? 'error' : undefined }
                    bottomLabel={
                        showNumberError ? (
                            <span>
                                <img style={{ marginRight: '0.5rem'}} src={ErrorIcon}
                                    className={Styles.errorIcon} alt="" />
                                {t('validCountryMsg')}
                            </span>
                        ) : undefined
                    }
                />
                {/* call reference Id*/}
                <MultiSelect
                    value={callReferenceId}
                    onChange={(val: any) => {
                        if(val?.length){
                            const result = val.filter((ele:any) => ele.trim().length > 0);
                            if(result?.length){
                                setCallReferenceId(result);
                            }
                        }
                        else{
                            setCallReferenceId(val);
                        }
                    }}
                    defaultValue={defaultCallId}
                    placeholder={t('callID')}
                    className={cn("mt-3")}
                    onSearch={(e)=>{
                        setCallIdSearchValue(e);
                        debouncedChange(e);
                    }}
                    searchValue={callIdSearchValue}
                    maxTagCount={3}
                >
                    {!isEmpty(callLogsIdsData) &&
                    callLogsIdsData.map(
                        (option:any) => {
                            return (
                                <MultiSelectOption
                                    key={option.call_id}
                                    value={option.call_id}
                                >
                                    {option.call_id}
                                </MultiSelectOption>
                            );
                        }
                    )}
                </MultiSelect>
                <MultiSelect
                    value={flowName}
                    selectedValues={flowName}
                    onChange={(val: any) => {
                        if (val?.length) {
                            const result = val.filter((ele: any) => ele.trim().length > 0);
                            if (result?.length) {
                                setFlowName(result);
                            }
                        }
                        else {
                            setFlowName(val);
                        }
                    }}
                    mode="multiple"
                    defaultValue={defaultFlowName}
                    placeholder={t('KLR00435')}
                    className={cn("mt-3")}
                    maxTagCount={6}
                >
                    {!isEmpty(ivrFlowNameList) &&
                        Object.values(ivrFlowNameList).map((option) => {
                            return (
                                <MultiSelectOption key={option} value={option}>
                                    {option}
                                </MultiSelectOption>
                            );
                    })}
                </MultiSelect>
                <Select
                    value={status}
                    onChange={(val: any) => {
                        setStatus(val);
                    }}
                    defaultValue={status}
                    placeholder={t('mainStatus')}
                    className={cn("mt-3")}
                    label={t('mainStatus')}
                >
                    {statusOptions.map((option) => {
                        return (
                            <SelectOption
                                key={option.value}
                                value={option.value}
                            >
                                {option.key}
                            </SelectOption>
                        );
                    })}
                </Select>
            </div>}
        </Slider>
    );
};

export default FilterModal;
